import { GAME_2048_DISPLAY_KEY, type Game2048DisplayMode } from '../../lib/game2048Appearance';
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useDailyPuzzleRollover } from '../../hooks/useDailyPuzzleRollover';
import { useDialogKeyboardNavigation } from '../../hooks/useDialogKeyboardNavigation';
import { use2048Appearance } from '../../hooks/use2048Appearance';
import { useSound } from '../../hooks/useSound';
import { use2048Persistence } from '../../hooks/use2048Persistence';
import { createDailyPuzzleIdentity, parseDailyPuzzleIdentity, type DailyPuzzleIdentity } from '../../lib/dailyPuzzle';
import { getDateSeed } from '../../lib/dateSeed';
import {
  double2048Tiles,
  get2048LargestTile,
  get2048Progress,
  get2048Target,
  move2048,
  type Game2048Direction,
  type Game2048MoveResult
} from '../../lib/game2048';
import {
  advance2048Session,
  archive2048Session,
  create2048Session,
  GAME_2048_STORAGE,
  load2048Session,
  parse2048Integer,
  parse2048Session,
  persist2048Session,
  read2048PendingRuns,
  undo2048Session,
  type Game2048Session
} from '../../lib/game2048Session';
import { createTimerTick, formatDisplayDate } from '../../lib/gameUtils';
import { isDevelopment } from '../../lib/utils';
import { Game2048GameView, type Game2048Animation } from './Game2048GameView';

interface Game2048GameProps {
  initialSeed?: number;
  puzzleDate?: Date | null;
  difficulty?: number | null;
  isPreviewMode?: boolean;
}

const startingIdentity = (props: Game2048GameProps): DailyPuzzleIdentity => {
  const date = props.puzzleDate ?? new Date();
  const difficulty = props.difficulty ?? 1;
  get2048Target(difficulty);
  const identity = createDailyPuzzleIdentity('2048', date, difficulty, props.initialSeed ?? getDateSeed(date, difficulty));
  if (props.initialSeed === undefined && !props.isPreviewMode) {
    const stored = localStorage.getItem(GAME_2048_STORAGE.identity);
    if (stored !== null) {
      const previous = parseDailyPuzzleIdentity(stored);
      if (previous.gameType === '2048') {
        if (previous.date === identity.date) {
          return previous;
        }
      }
    }
  }
  return identity;
};

export const Game2048Game = (props: Game2048GameProps = {}) => {
  const { initialSeed, isPreviewMode = false } = props;
  const { t, i18n } = useTranslation();
  const [session, setSession] = useState(() => {
    const identity = startingIdentity(props);
    return isPreviewMode ? create2048Session(identity) : load2048Session(identity);
  });
  const sessionRef = useRef(session);
  const mountedRef = useRef(true);
  const [animation, setAnimation] = useState<Game2048Animation | null>(null);
  const [isPaused, setPaused] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [celebrateTopped, setCelebrateTopped] = useState(false);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const { play: playMoveSound } = useSound('/assets/sounds/tile-move.mp3');
  const { play: playCompleteSound } = useSound('/assets/sounds/puzzle-complete.mp3');

  const replaceSession = useCallback((next: Game2048Session) => {
    sessionRef.current = next;
    if (!isPreviewMode) {
      persist2048Session(next);
    }
    setSession(next);
  }, [isPreviewMode]);

  const isDisplayLocked = session.moves > 0 || session.undosUsed > 0 || session.outcome !== 'active';
  const changeDisplayMode = (displayMode: Game2048DisplayMode) => {
    const current = sessionRef.current;
    if (current.moves > 0 || current.undosUsed > 0 || current.outcome !== 'active') {
      throw new Error('Cannot change 2048 display mode after the run has started');
    }
    localStorage.setItem(GAME_2048_DISPLAY_KEY, displayMode);
    replaceSession({ ...current, displayMode });
  };
  const artwork = use2048Appearance(session.identity.date, session.displayMode, changeDisplayMode, isDisplayLocked);

  const updateRun = useCallback((runId: string, update: (run: Game2048Session) => Game2048Session) => {
    if (isPreviewMode) {
      return;
    }
    const serialized = localStorage.getItem(GAME_2048_STORAGE.session);
    const stored = serialized === null ? null : parse2048Session(serialized);
    if (stored?.runId === runId) {
      const next = update(stored);
      if (mountedRef.current && sessionRef.current.runId === runId) {
        replaceSession(next);
      } else {
        persist2048Session(next);
      }
      return;
    }
    const archived = read2048PendingRuns().find(run => run.runId === runId);
    if (archived) {
      archive2048Session(update(archived));
    }
  }, [isPreviewMode, replaceSession]);

  const persistence = use2048Persistence({ session, updateRun, isPreviewMode });
  const { target, difficulty } = get2048Progress(session.highestTile);
  const isTerminal = session.outcome !== 'active';
  const isInputLocked = isPaused || showResetDialog || showCompletionDialog || isTerminal || !persistence.canPlay || artwork.isBlocking;
  const lockRef = useRef(isInputLocked);
  lockRef.current = isInputLocked;
  const currentPuzzleDate = useMemo(() => new Date(session.identity.date + 'T00:00:00'), [session.identity.date]);
  const completionDialogRef = useDialogKeyboardNavigation(showCompletionDialog, () => setShowCompletionDialog(false));
  const resetDialogRef = useDialogKeyboardNavigation(showResetDialog, () => setShowResetDialog(false));

  useEffect(() => {
    mountedRef.current = true;
    if (!isPreviewMode) {
      persist2048Session(sessionRef.current);
    }
    return () => { mountedRef.current = false; };
  }, [isPreviewMode]);

  const resetToIdentity = useCallback((identity: DailyPuzzleIdentity) => {
    if (!isPreviewMode) {
      archive2048Session(sessionRef.current);
    }
    replaceSession(create2048Session(identity, persistence.principal));
    setAnimation(null);
    setPaused(false);
    setShowResetDialog(false);
    setShowCompletionDialog(false);
    setCelebrateTopped(false);
  }, [isPreviewMode, persistence.principal, replaceSession]);

  const applyMove = useCallback((result: Game2048MoveResult) => {
    if (!result.moved) {
      return;
    }
    const previous = sessionRef.current;
    const next = advance2048Session(previous, result);
    replaceSession(next);
    setAnimation({ motions: result.motions, spawnedTile: result.spawnedTile });
    if (previous.highestTile < 512 && next.highestTile >= 512 && next.outcome !== 'topped') {
      lockRef.current = true;
      setShowCompletionDialog(true);
    }
    if (next.outcome === 'topped') {
      lockRef.current = true;
      setCelebrateTopped(true);
      playCompleteSound();
    } else if (next.highestTile >= 512 && next.highestTile > previous.highestTile) {
      playCompleteSound();
      toast.success(t('2048.milestoneReached', { target: next.highestTile, next: get2048Progress(next.highestTile).target }));
    } else {
      playMoveSound();
    }
    if (next.outcome === 'lost') {
      lockRef.current = true;
    }
  }, [playCompleteSound, playMoveSound, replaceSession, t]);

  const move = useCallback((direction: Game2048Direction) => {
    if (!lockRef.current) {
      applyMove(move2048(sessionRef.current.engine, direction));
    }
  }, [applyMove]);

  const win = useCallback(() => {
    if (isDevelopment() && !isPreviewMode && !lockRef.current) {
      applyMove(double2048Tiles(sessionRef.current.engine));
    }
  }, [applyMove, isPreviewMode]);

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    const directions: Partial<Record<string, Game2048Direction>> = {
      ArrowUp: 'up', w: 'up', W: 'up',
      ArrowDown: 'down', s: 'down', S: 'down',
      ArrowLeft: 'left', a: 'left', A: 'left',
      ArrowRight: 'right', d: 'right', D: 'right'
    };
    const direction = directions[event.key];
    if (direction) {
      event.preventDefault();
      move(direction);
    }
  }, [move]);

  const handlePointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
    pointerStartRef.current = { x: event.clientX, y: event.clientY };
  }, []);

  const handlePointerUp = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const start = pointerStartRef.current;
    pointerStartRef.current = null;
    if (!start) {
      return;
    }
    const horizontal = event.clientX - start.x;
    const vertical = event.clientY - start.y;
    if (Math.max(Math.abs(horizontal), Math.abs(vertical)) < 30) {
      return;
    }
    move(Math.abs(horizontal) > Math.abs(vertical)
      ? (horizontal > 0 ? 'right' : 'left')
      : (vertical > 0 ? 'down' : 'up'));
  }, [move]);

  const requestReset = useCallback(() => {
    if (sessionRef.current.engine.moves === 0 || sessionRef.current.outcome !== 'active') {
      resetToIdentity(sessionRef.current.identity);
    } else {
      lockRef.current = true;
      setShowResetDialog(true);
    }
  }, [resetToIdentity]);

  const handleDailyDateCheck = useCallback((now: Date) => {
    const identity = createDailyPuzzleIdentity('2048', now, 1);
    const current = sessionRef.current;
    if (identity.date === current.identity.date) {
      return;
    }
    if (current.outcome === 'active' && (current.moves > 0 || current.undosUsed > 0)) {
      return;
    }
    resetToIdentity(identity);
  }, [resetToIdentity]);

  useDailyPuzzleRollover(initialSeed === undefined && !isPreviewMode, handleDailyDateCheck);

  useEffect(() => {
    if (animation === null) {
      return;
    }
    const animationTimer = window.setTimeout(() => setAnimation(null), 240);
    return () => window.clearTimeout(animationTimer);
  }, [animation]);

  useEffect(() => {
    if (isInputLocked || isPreviewMode) {
      return;
    }
    const tick = createTimerTick(nextTime => {
      const current = sessionRef.current;
      const elapsedTime = typeof nextTime === 'function' ? nextTime(current.elapsedTime) : nextTime;
      if (!Number.isSafeInteger(elapsedTime)) {
        throw new Error('Unsafe 2048 elapsed time');
      }
      replaceSession({ ...current, elapsedTime });
    }, '2048');
    const timer = window.setInterval(() => {
      if (!lockRef.current) {
        tick();
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [isInputLocked, isPreviewMode, replaceSession]);

  const bestScore = parse2048Integer(localStorage.getItem(GAME_2048_STORAGE.bestScore), 'best score');
  const statusText = isPaused ? t('gamePaused')
    : isTerminal ? t(session.outcome === 'topped' ? '2048.topped' : '2048.gameOverStatus')
      : t('2048.currentStatus', { score: session.engine.score, largestTile: get2048LargestTile(session.engine.board) });

  return (
    <Game2048GameView
      artwork={artwork}
      title={formatDisplayDate(currentPuzzleDate, t, i18n, '2048', session.highestTile >= 512, isPreviewMode)}
      engine={session.engine}
      animation={animation}
      target={target}
      highestTile={session.highestTile}
      bestScore={bestScore}
      personalRecord={persistence.personalRecord}
      recordMessage={persistence.recordMessage}
      persistenceError={persistence.error}
      onRetrySave={persistence.retry}
      elapsedTime={session.elapsedTime}
      difficulty={difficulty}
      seed={session.identity.seed}
      incompleteGames={persistence.incompleteGames}
      isPreviewMode={isPreviewMode}
      isCompleted={session.highestTile >= 512}
      outcome={session.outcome}
      celebrateTopped={celebrateTopped}
      isInputLocked={isInputLocked}
      isPaused={isPaused}
      canUndo={session.previousEngine !== null}
      showCompletionDialog={showCompletionDialog}
      completionDialogRef={completionDialogRef}
      onCloseCompletion={() => setShowCompletionDialog(false)}
      showResetDialog={showResetDialog}
      statusText={statusText}
      resetDialogRef={resetDialogRef}
      onMove={move}
      onWin={win}
      onKeyDown={handleKeyDown}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => { pointerStartRef.current = null; }}
      onTogglePause={() => {
        if (!isTerminal && !showResetDialog && !showCompletionDialog && persistence.canPlay) {
          lockRef.current = !isPaused;
          setPaused(!isPaused);
        }
      }}
      onUndo={() => {
        if (!lockRef.current) {
          replaceSession(undo2048Session(sessionRef.current));
          setAnimation(null);
        }
      }}
      onReset={requestReset}
      onCloseReset={() => setShowResetDialog(false)}
      onConfirmReset={() => resetToIdentity(sessionRef.current.identity)}
    />
  );
};
